import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SrpTopEmployersStrip } from './SrpTopEmployersStrip';
import { useTopEmployersStripData } from '../hooks/useTopEmployersStripData';
import type { SrpTopEmployersStripItem } from '../types/srp-top-employers-strip';

export interface TopEmployersStripWithClientFetchProps {
  /** From slim/full page data when present; otherwise fetched client-side. */
  initialEmployers?: SrpTopEmployersStripItem[] | null;
  locale: string;
  /** When true, do not fetch or show hub strip (location header / single-location SRP). */
  hideStrip?: boolean;
}

/**
 * Top-employers teaser on the SearchResultsPage / static SRP paths: fills from SSR when available,
 * otherwise loads `/api/top-employers-strip` after navigation.
 */
export function TopEmployersStripWithClientFetch({
  initialEmployers,
  locale,
  hideStrip = false,
}: TopEmployersStripWithClientFetchProps): React.ReactElement | null {
  const intl = useIntl();
  const { employers, isPending } = useTopEmployersStripData(initialEmployers, {
    allowClientFetch: !hideStrip,
  });

  const labels = useMemo(
    () => ({
      label: intl.formatMessage({
        id: 'search.bestCompaniesStrip.label',
        defaultMessage: 'Top employers',
        description: 'Heading for the compact top-employers row on the search results page',
      }),
      hubCta: intl.formatMessage({
        id: 'search.bestCompaniesStrip.hubCta',
        defaultMessage: 'Best employers for Finland roles →',
        description: 'Link from SRP top-employers teaser to the best-companies hub (Finland-targeted listings)',
      }),
    }),
    [intl]
  );

  return <SrpTopEmployersStrip employers={employers} locale={locale} labels={labels} isPending={isPending} />;
}
